import React from 'react'
import { StackView } from '@planningcenter/tapestry-react'
import {
  PageHeading,
  Heading,
  Text,
  List,
  Code,
  CodeBlock,
  Link,
} from '../../shared/typography'
import { NavLink } from 'react-router-dom'

const page = () => {
  return (
    <StackView axis="vertical" spacing={2}>
      <PageHeading>JSON-API</PageHeading>

      <Text>
        This API is built to conform to the{' '}
        <Link to="http://jsonapi.org/" external>
          JSON API
        </Link>{' '}
        1.0 specification.
      </Text>

      <Text weight={600}>Some benefits:</Text>

      <List>
        <List.Item>
          <Text>
            Excellent documentation is already available{' '}
            <Link to="http://jsonapi.org/format/" external>
              here
            </Link>{' '}
            and we do our best to stay true to it.
          </Text>
        </List.Item>
        <List.Item>
          <Text>
            Our API returns resources that contain links to related resources,
            making it easier to “browse” this API and learn about associated
            endpoints.
          </Text>
        </List.Item>
        <List.Item>
          <Text>
            You can include related resources by adding{' '}
            <Code>?include=something</Code> to the URL. This makes grabbing what
            you need much simpler.
          </Text>
        </List.Item>
      </List>

      <Heading anchored id="data-format" size={1} level={2}>
        Data Format
      </Heading>

      <Text>
        Resources are wrapped in an object that always has at least a{' '}
        <Code>data</Code> key.
      </Text>
      <Text>
        For an endpoint that returns multiple resources, e.g.{' '}
        <Code>/people/v2/people</Code>, the result will be an object having a{' '}
        <Code>data</Code> key with an array for its value:
      </Text>
      <CodeBlock language="json">
        {`{
  "data": [
    {
      "type": "Thing",
      "id": "1",
      "attributes": { /*..*/ }
    },
    {
      "type": "Thing",
      "id": "2",
      "attributes": { /*..*/ }
    }
  ]
}`}
      </CodeBlock>
      <Text>
        For an endpoint that returns a single resource, e.g.{' '}
        <Code>/people/v2/people/123</Code>, the result will be an object having
        a <Code>data</Code> key with an object for its value:
      </Text>
      <CodeBlock language="json">
        {`{
  "data": {
    "type": "Thing",
    "id": "1",
    "attributes": { /*..*/ }
  }
}`}
      </CodeBlock>
      <Text>
        Creating or updating resources also requires that same structure. Be
        sure to wrap the payload with the same{' '}
        <Code>{'{ "data": { ... } }'}</Code> wrapper and put the resource’s
        attributes inside the “attributes” object.
      </Text>

      <Heading anchored id="filtering-results" size={1} level={2}>
        Filtering Results
      </Heading>
      <Text>
        Filtering results of an endpoint can be done a few different ways:
        either by using <Code>where</Code> or with a <Code>filter</Code>.
      </Text>
      <Heading anchored id="where" level={3}>Where</Heading>
      <Text>
        Querying by <Code>where</Code> lets you restrict the results of an
        endpoint based on the value of one or more attributes. The format is{' '}
        <Code>where[ATTRIBUTE]=VALUE</Code>. By default, the condition is a
        simple equality check, e.g.
        <Code>
          https://api.planningcenteronline.com/people/v2/people?where[first_name]=Tim
        </Code>
        will return people whose first name exactly matches "Tim" (case
        insensitively).
      </Text>
      <Text>
        Adding a URL-escaped ʼ%ʼ character (encoded as <Code>%25</Code>) to the
        beginning or end of the value lets you match part of a string, e.g.
        <Code>
          https://api.planningcenteronline.com/people/v2/people?where[first_name]=Tim%25
        </Code>
        will return people whose first name starts with "Tim", and thus would
        match Tim, Timmy, Timothy, etc.{' '}
        <em>
          Note there are some attributes that do not support this operator, due
          to performance reasons.
        </em>
      </Text>
      <Text>
        You can query for resources before or after certain dates and times by
        using
        <Code>gt</Code>, <Code>gte</Code>, <Code>lt</Code>, and <Code>lte</Code>
        . The construction of these operators is of the format{' '}
        <Code>where[ATTRIBUTE][OPERATOR]=VALUE</Code>, e.g.
        <Code>
          https://api.planningcenteronline.com/people/v2/people?where[birthdate][lte]=1981-04-28
        </Code>
        will return people whose birthdate is on or before (less-than-or-equal
        to) April 28, 1981. You can learn more in the{' '}
        <NavLink to="/overview/dates-times">Dates &amp; Times</NavLink> section
        of the documentation.
      </Text>
      <Text>
        Lastly, you can query based on included resources (see the{' '}
        <strong>Including Related Resources</strong> section below) by using an
        additional set of square brackets of the format
        <Code>where[ASSOCIATION][ATTRIBUTE]=VALUE</Code>, e.g.
        <Code>
          https://api.planningcenteronline.com/people/v2/people?include=emails&where[emails][address]=tim@example.com
        </Code>
        will return people who have an email address equal to "tim@example.com".
      </Text>
      <Text>
        Attributes available to query by <Code>where</Code> are documented in
        the <strong>Query By</strong> section of the API documentation for a
        resource.
      </Text>
      <Heading anchored id="filter" level={3}>Filter</Heading>
      <Text>
        Filtering is a way for an endpoint to provide advanced querying that a
        simple <Code>where</Code> might not allow. Use the <Code>filter</Code>{' '}
        parameter to enable a filter, e.g.{' '}
        <Code>
          https://api.planningcenteronline.com/people/v2/people/13/messages?filter=received
        </Code>
        . Available filters are documented in the <strong>Associations</strong>{' '}
        section of the API documentation for a resource, under the{' '}
        <strong>Filter By</strong> column.
      </Text>

      <Heading anchored id="including-related-resources" size={1} level={2}>
        Including Related Resources
      </Heading>
      <Text>
        Often, an endpoint will allow you to query a parent resource along with
        some child or other linked resource. For instance, you can query both a
        Person resource and all the associated email addresses (a one-to-many
        relationship) in a single query. You can also query a FieldDatum along
        with its associated FieldDefinition (a many-to-one relationship).
      </Text>
      <Text>
        You use the <Code>include</Code> parameter to specify what resources to
        include, each one separated by a comma, e.g.:
        <Code>
          https://api.planningcenteronline.com/people/v2/people?include=emails,phone_numbers
        </Code>
        , which returns results like this:
      </Text>
      <CodeBlock language="json">
        {`
{
  "data": [
    {
      "type": "Person",
      "id": "13",
      "attributes": {
        /* ... */
      },
      "relationships": {
        "emails": {
          "data": [
            {
              "type": "Email",
              "id": "11914559"
            },
            {
              "type": "Email",
              "id": "53967483"
            }
          ]
        },
        "phone_numbers": {
          "data": [
            {
              "type": "PhoneNumber",
              "id": "6706677"
            }
          ]
        }
      }
    }
  ],
  "included": [
    {
      "type": "Email",
      "id": "11914559",
      "attributes": {
        "address": "matt@example.com",
        /* ... */
      }
    },
    {
      "type": "Email",
      "id": "53967483",
      "attributes": {
        "address": "matthew@example.com",
        /* ... */
      }
    },
    {
      "type": "PhoneNumber",
      "id": "6706677",
      "attributes": {
        "number": "(123) 456-7890",
        /* ... */
      }
    }
  ]
}`}
      </CodeBlock>
      <Text>
        You can read more about the <Code>include</Code> parameter in the{' '}
        <Link to="https://jsonapi.org/format/#fetching-includes" external>
          JSON API documentation
        </Link>
        .
      </Text>
    </StackView>
  )
}

export default page
